import { FFHelpers } from '@jlafica/rcform';
import { dateHandler } from "../utilities/date-utils";
import { boolToInt, deepClone } from "../utilities/general-utils";

export const fieldNames = {
    title: "title",
    description: "description",
    amount: "amount",
    category: "category",
    subcategory: "subcategory",
    account: "account",
    startDate: "startDate",
    endDate: "endDate",
    isRecurring: "isRecurring",
    recurringType: "recurringType",
    dayOfWeek: "dayOfWeek",
    dayOfMonthList: "dayOfMonthList",
    dateFrequencyType: "dateFrequencyType",
    weekOfMonth: "weekOfMonth",
    monthOfYear: "monthOfYear",
    separationCount: "separationCount",
}

export const initialState = {
    [fieldNames.title]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.description]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.amount]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.category]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.subcategory]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.account]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.startDate]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.endDate]: {
        ...FFHelpers.ffInitialFieldState.input
    },
    [fieldNames.isRecurring]: {
        ...FFHelpers.ffInitialFieldState.checkbox
    },
    [fieldNames.recurringType]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.dayOfWeek]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.dayOfMonthList]: {
        ...FFHelpers.ffInitialFieldState.checkboxGroup
    },
    [fieldNames.dateFrequencyType]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.weekOfMonth]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.monthOfYear]: {
        ...FFHelpers.ffInitialFieldState.select
    },
    [fieldNames.separationCount]: {
        ...FFHelpers.ffInitialFieldState.input
    },
}

export const getTransactionFormFieldsModel = (transaction) => {
    let dayOfMonthList = [];
    let dateFrequencyType = 0;

    if (transaction.dayOfMonthList) {
        if (typeof transaction.dayOfMonthList === "string") {
            dayOfMonthList = transaction.dayOfMonthList.split(",").map(day => parseInt(day));
        }
        else if (Array.isArray(transaction.dayOfMonthList)) {
            dayOfMonthList = transaction.dayOfMonthList;
        }
        else if (typeof transaction.dayOfMonthList === "number") {
            dayOfMonthList = [transaction.dayOfMonthList];
        }
    }

    if (transaction.recurringType === "monthly" || transaction.recurringType === "yearly") {
        if (dayOfMonthList.length > 0) {
            dateFrequencyType = 1;
        }
        else if (Number.isInteger(transaction.dayOfWeek)) {
            dateFrequencyType = 2;
        }
    }

    return {
        [fieldNames.title]: {
            value: transaction.title ?? ''
        },
        [fieldNames.description]: {
            value: transaction.description ?? ''
        },
        [fieldNames.amount]: {
            value: transaction.amount ?? 0
        },
        [fieldNames.category]: {
            value: transaction.category ?? ''
        },
        [fieldNames.subcategory]: {
            value: transaction.subcategory ?? ''
        },
        [fieldNames.account]: {
            value: transaction.account ?? ''
        },
        [fieldNames.startDate]: {
            value: transaction.startDate ?? ''
        },
        [fieldNames.endDate]: {
            value: transaction.endDate ?? ''
        },
        [fieldNames.isRecurring]: {
            value: transaction.isRecurring ?? 0,
            checked: transaction.isRecurring === 1
        },
        [fieldNames.recurringType]: {
            value: transaction.recurringType ?? null
        },
        [fieldNames.dayOfWeek]: {
            value: transaction.dayOfWeek ?? null
        },
        [fieldNames.dayOfMonthList]: {
            selectedValues: dayOfMonthList
        },
        [fieldNames.dateFrequencyType]: {
            value: dateFrequencyType
        },
        [fieldNames.weekOfMonth]: {
            value: transaction.weekOfMonth ?? null
        },
        [fieldNames.monthOfYear]: {
            value: transaction.monthOfYear ?? null
        },
        [fieldNames.separationCount]: {
            value: transaction.separationCount ?? null
        },
    }
}

export const getTransactionRawModelFromFieldValues = (id, formState) => {
    return {
        id: id,
        title: formState.fields[fieldNames.title].value,
        description: formState.fields[fieldNames.description].value,
        amount: parseFloat(formState.fields[fieldNames.amount].value).toFixed(2),
        category: formState.fields[fieldNames.category].value,
        subcategory: formState.fields[fieldNames.subcategory].value,
        account: formState.fields[fieldNames.account].value,
        start_date: formState.fields[fieldNames.startDate].value
            ? dateHandler.toSubmitFormat(formState.fields[fieldNames.startDate].value)
            : null,
        end_date: formState.fields[fieldNames.endDate].value
            ? dateHandler.toSubmitFormat(formState.fields[fieldNames.endDate].value)
            : null,
        is_recurring: boolToInt(formState.fields[fieldNames.isRecurring].checked),
        recurring_type: formState.fields[fieldNames.recurringType].value,
        day_of_week: formState.fields[fieldNames.dayOfWeek].value,
        day_of_month_list: formState.fields[fieldNames.dayOfMonthList].selectedValues,
        week_of_month: formState.fields[fieldNames.weekOfMonth].value,
        month_of_year: formState.fields[fieldNames.monthOfYear].value,
        separation_count: formState.fields[fieldNames.separationCount].value,
    }
}

export const getTransactionSubmissionModel = (id, formState) => {
    const returnModel = {
        id: id,
        title: formState.fields[fieldNames.title].value,
        description: formState.fields[fieldNames.description].value,
        amount: parseFloat(formState.fields[fieldNames.amount].value).toFixed(2),
        category: formState.fields[fieldNames.category].value,
        subcategory: formState.fields[fieldNames.subcategory].value,
        account: formState.fields[fieldNames.account].value,
        startDate: formState.fields[fieldNames.startDate].value
            ? dateHandler.toSubmitFormat(formState.fields[fieldNames.startDate].value)
            : null,
        endDate: formState.fields[fieldNames.endDate].value
            ? dateHandler.toSubmitFormat(formState.fields[fieldNames.endDate].value)
            : null,
        isRecurring: 0,
        recurringType: null,
        dayOfWeek: null,
        dayOfMonthList: null,
        weekOfMonth: null,
        monthOfYear: null,
        separationCount: null,
        isSnowball: 0,
    }

    // If not recurring, return the model as is
    if (formState.fields[fieldNames.isRecurring].checked === false) {
        return returnModel
    }

    const recurringType = formState.fields[fieldNames.recurringType].value;

    const {
        dayOfWeek,
        dayOfMonthList,
        weekOfMonth,
        monthOfYear
    } = getRecurrenceValuesBasedOnRecurringType(recurringType, formState);

    // If recurring, return the model with the recurring values
    return {
        ...returnModel,
        isRecurring: 1,
        recurringType: recurringType,
        dayOfWeek: dayOfWeek,
        dayOfMonthList: dayOfMonthList,
        weekOfMonth: weekOfMonth,
        monthOfYear: monthOfYear,
        separationCount: formState.fields[fieldNames.separationCount].value,
        isSnowball: 0,
    }
}

const getRecurrenceValuesBasedOnRecurringType = (recurringType, formState) => {
    const fieldsClone = deepClone(formState.fields);
    
    const dateFrequencyType = parseInt(fieldsClone[fieldNames.dateFrequencyType].value);
    let dayOfWeek = fieldsClone[fieldNames.dayOfWeek].value;
    let dayOfMonthList = fieldsClone[fieldNames.dayOfMonthList].selectedValues;
    let weekOfMonth = fieldsClone[fieldNames.weekOfMonth].value;
    let monthOfYear = fieldsClone[fieldNames.monthOfYear].value;

    if (recurringType === "daily") {
        dayOfWeek = null;
        dayOfMonthList = null;
        weekOfMonth = null;
        monthOfYear = null;
    } else if (recurringType === "weekly") {
        dayOfWeek = parseInt(dayOfWeek);
        dayOfMonthList = null;
        weekOfMonth = null;
        monthOfYear = null;
    } else if (recurringType === "monthly") {
        monthOfYear = null;

        if (dateFrequencyType === 1) {
            dayOfWeek = null;
            weekOfMonth = null;
        } else if (dateFrequencyType === 2) {
            dayOfWeek = parseInt(dayOfWeek);
            dayOfMonthList = null;
        }
    } else if (recurringType === "yearly") {
        monthOfYear = parseInt(monthOfYear);

        if (dateFrequencyType === 1) {
            dayOfWeek = null;
            weekOfMonth = null;
        } else if (dateFrequencyType === 2) {
            dayOfWeek = parseInt(dayOfWeek);
            dayOfMonthList = null;
        }
    }

    return {
        dayOfWeek,
        dayOfMonthList,
        weekOfMonth,
        monthOfYear
    }
}

const dayOfWeekWeeklyOptions = [
    { value: '', label: 'Select a day' },
    { value: 0,  label: 'Sunday'       },
    { value: 1,  label: 'Monday'       },
    { value: 2,  label: 'Tuesday'      },
    { value: 3,  label: 'Wednesday'    },
    { value: 4,  label: 'Thursday'     },
    { value: 5,  label: 'Friday'       },
    { value: 6,  label: 'Saturday'     }
];

const dayOfWeekMonthlyOptions = [
    ...dayOfWeekWeeklyOptions,
    { value: 7, label: 'Day'         },
    { value: 8, label: 'Weekday'     },
    { value: 9, label: 'Weekend Day' }
];

export const dayOfWeekFieldOptions = {
    weekly:  [ ...dayOfWeekWeeklyOptions  ],
    monthly: [ ...dayOfWeekMonthlyOptions ]
};